/**
 * Pure helpers for @-mentions in chat messages.
 *
 * A message carries `mentions?: string[]` — user ids only, no names. The text
 * itself holds the human form (`@Asha R`), inserted verbatim by the composer.
 * Rendering therefore needs both halves: resolve each mentioned id to the name
 * the composer would have inserted, then split the content on those `@Name`
 * tokens so they can be styled.
 *
 * Id caveat (see `commsParticipantId.js`): ms-agents may hand out either a
 * coach's `user_id` or their `employee_id` for the same person, so every id
 * comparison here is a loose String() match against both participant forms.
 * Names come from the participant record when present, else the shared
 * identity cache via `resolveName` — an id that resolves to no name is simply
 * dropped, and its `@` text renders as plain content rather than crashing or
 * leaking a raw id.
 */
import { isOwnSender, participantId } from "./commsParticipantId";

/**
 * Is the viewer among a message's mentioned users?
 *
 * Retraction is deliberately NOT considered here — callers that suppress
 * highlights on retracted messages gate on that themselves.
 *
 * @param {Object|null|undefined} message
 * @param {string|number|null|undefined} viewerUserId
 * @returns {boolean}
 */
export const mentionsViewer = (message, viewerUserId) => {
  if (!message || viewerUserId == null || !Array.isArray(message.mentions)) {
    return false;
  }
  const viewer = String(viewerUserId);
  return message.mentions.some((id) => id != null && String(id) === viewer);
};

/**
 * Does any of these messages @-mention the viewer, ignoring the viewer's own
 * sends? Used to decide whether a room with unread messages should show the
 * "tagged" treatment — the rooms listing carries no mention field, so the
 * unread tail has to be inspected directly.
 *
 * @param {Array<Object>|null|undefined} messages
 * @param {string|number|null|undefined} viewerUserId
 * @returns {boolean}
 */
export const hasMentionForViewer = (messages, viewerUserId) =>
  Array.isArray(messages) &&
  messages.some(
    (message) =>
      !message?.retracted &&
      !isOwnSender(message, viewerUserId) &&
      mentionsViewer(message, viewerUserId)
  );

/**
 * Does this participant record answer to the given mention id?
 * Either id form can appear in `mentions`, so match both.
 */
const participantMatches = (participant, mentionId) => {
  const id = String(mentionId);
  return (
    (participant?.resolved_user_id != null &&
      String(participant.resolved_user_id) === id) ||
    (participant?.user_id != null && String(participant.user_id) === id)
  );
};

/**
 * Resolve a message's mentioned ids to `{ userId, name }` targets for the
 * tokenizer. Unresolvable ids are dropped (their text stays plain).
 *
 * @param {Object} args
 * @param {Object|null|undefined} args.message - Raw message (reads `mentions`).
 * @param {Array<Object>} [args.participants=[]] - Room participant records.
 * @param {(userId: string, role: string) => string|undefined} [args.resolveName] -
 *   Shared identity-cache lookup (same contract as `commsSenderName`).
 * @returns {Array<{ userId: string, name: string }>}
 */
export const buildMentionTargets = ({ message, participants = [], resolveName }) => {
  if (!message || !Array.isArray(message.mentions)) return [];

  const targets = [];
  for (const mentionId of message.mentions) {
    if (mentionId == null) continue;

    const participant = participants.find((p) => participantMatches(p, mentionId));
    const name =
      (typeof participant?.name === "string" && participant.name.trim()) ||
      (typeof resolveName === "function" &&
        resolveName(
          String(participant ? participantId(participant) : mentionId),
          participant?.participant_role || "COACH"
        )) ||
      "";

    if (typeof name === "string" && name.trim()) {
      targets.push({ userId: String(mentionId), name: name.trim() });
    }
  }
  return targets;
};

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Split message content into text and mention tokens.
 *
 * Matching is exact-case (the composer inserts the exact resolved name) and
 * longest-name-first, so `@Samantha Lee` is never half-claimed by a `@Sam`
 * target. A name mentioned more than once tokenizes every occurrence.
 *
 * @param {string|null|undefined} content
 * @param {Array<{ userId: string, name: string }>} targets
 * @returns {Array<{ type: "text"|"mention", text: string, userId?: string }>}
 */
export const tokenizeMentions = (content, targets) => {
  if (typeof content !== "string" || content === "") return [];
  if (!Array.isArray(targets) || targets.length === 0) {
    return [{ type: "text", text: content }];
  }

  const byToken = new Map(
    targets.map(({ userId, name }) => [`@${name}`, userId])
  );
  const alternation = [...byToken.keys()]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
    .join("|");
  const pattern = new RegExp(`(${alternation})`, "g");

  const tokens = [];
  let lastIndex = 0;
  let match;
  while ((match = pattern.exec(content)) !== null) {
    if (match.index > lastIndex) {
      tokens.push({ type: "text", text: content.slice(lastIndex, match.index) });
    }
    tokens.push({ type: "mention", text: match[0], userId: byToken.get(match[0]) });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < content.length) {
    tokens.push({ type: "text", text: content.slice(lastIndex) });
  }
  return tokens.length ? tokens : [{ type: "text", text: content }];
};
